import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import useAsyncEffect from 'use-async-effect';
import { useDispatch, useSelector } from 'react-redux';
import api from '../../api/api';
import { to } from '../../util/util';
import { IGlobalState } from '../../model/IGlobalState';
import { ShareRegistryProvider } from '../../model/ShareRegistryProvider';
import { ShareRegistryOptions } from '../../model/Stock';
import FormDateItem from '../form/FormDateItem';
import FormTextItem from '../form/FormTextItem';
import AnimatedSaveButton from '../AnimatedSaveButton';
import AlertEx from '../AlertEx';
import { startSetStock } from '../../actions/CurrentStock';
import { HoldingsMovementReportSummaryView as ReportSummary } from '../../model/HoldingsMovementReportResults';

interface IProps {
    ticker: string;
    exchange: string;
    combinedTicker: string;
}

const Wrapper = styled.div`
    table {
        width: 100%;
        border-collapse: collapse;
        margin-top: 1rem;
    }
    th, td {
        padding: 0.5rem 0.75rem;
        border-bottom: 1px solid #e3e8ee;
        text-align: left;
    }
    th {
        background: #f6f9fc;
        color: #333;
        font-weight: 600;
    }
    td.num, th.num {
        text-align: right;
        white-space: nowrap;
    }
    .badge {
        color: #fff;
        font-size: 10px;
        padding: 3px 8px;
        border-radius: 3px;
        margin-left: 6px;
        font-weight: 600;
        text-transform: uppercase;
    }
    .badge--new { background: #3498db; }
    .badge--existing { background: #27ae60; }
    .badge--returning { background: #e67e22; }
`;

const previousBusinessDay = () => {
    const d = moment().subtract(1, 'day');
    while (d.isoWeekday() > 5) d.subtract(1, 'day');
    return d.format('DD-MMM-YYYY');
}

const HoldingsMovementReports: React.FC<IProps> = (props: IProps) => {

    const currentStock = useSelector((state: IGlobalState) => state.currentStock);
    const dispatch = useDispatch();
    const [reports, setReports] = useState<ReportSummary[] | null>(null);
    const [error, setError] = useState('');
    // held locally and sent as the run date; not persisted on the stock
    const [reportDate, setReportDate] = useState<string | null>(previousBusinessDay());
    const [recipients, setRecipients] = useState<string>(currentStock.shareRegistryReportRecipients || '');
    const [options, setOptions] = useState<ShareRegistryOptions | null>(currentStock.shareRegistryOptions || null);

    useEffect(() => {
        setRecipients(currentStock.shareRegistryReportRecipients || '');
        setOptions(currentStock.shareRegistryOptions || null);
    }, [currentStock]);

    const saveSettings = async (): Promise<boolean> => {
        const result = await api().saveShareRegistryHoldingsMovementReportSettings(props.ticker, props.exchange, {
            shareRegistryReportRecipients: recipients,
            shareRegistryOptions: options,
        });
        if (result.success) {
            dispatch(startSetStock(props.combinedTicker));
            return true;
        }
        await AlertEx(result.errorMessage || 'Failed to save holdings movement report settings');
        return false;
    }

    const load = async () => {
        const [err, data] = await to(api().getHoldingsMovementReportSummaries(props.ticker, props.exchange));
        if (err || !data.success) {
            setError(err?.message || data?.message || 'Failed to fetch reports');
        } else {
            setReports(data.reports);
        }
    }

    useAsyncEffect(load, []);

    const showRunReport = (currentStock.shareRegistryProvider ?? ShareRegistryProvider.None) !== ShareRegistryProvider.None
        && !!reportDate;

    if (error) return <Wrapper><p style={{ color: '#c0392b' }}>{error}</p></Wrapper>;

    return <Wrapper>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '24px' }}>
            <div style={{ flex: 1 }}>
                <FormTextItem label="Report Recipients" name='shareRegistryReportRecipients' value={recipients}
                    onChange={(e: any) => setRecipients(e.target.value)} />
            </div>
            <div style={{ width: '340px' }}>
                <FormTextItem label="New Holder Min Value" helperText="$ for NEW/RETURNING badge" name='newHolderMinValue'
                    className='config__new-holder-min-value'
                    value={(options?.newHolderMinValue ?? 0).toLocaleString()}
                    onChange={(e: any) => setOptions({ ...options, newHolderMinValue: parseFloat(e.target.value.replace(/,/g, '')) || 0 })} />
            </div>
            <div style={{ marginBottom: '25px' }}>
                <AnimatedSaveButton onClick={saveSettings} />
            </div>
        </div>
        {showRunReport && <div style={{ display: 'flex', alignItems: 'flex-end', gap: '24px', marginTop: '2rem' }}>
            <FormDateItem label="Report date" name='shareRegistryReportDate' value={reportDate}
                onChange={(e: any) => setReportDate(e.target.value)} className='config__share-registry-date' />
            <div style={{ marginBottom: '25px' }}>
                <AnimatedSaveButton
                    startingText="Run Report"
                    progressText="Running..."
                    completedText="Done!"
                    onClick={async () => {
                        try {
                            const result = await api().runHoldingsMovementReport(props.ticker, props.exchange,
                                moment(reportDate, 'DD-MMM-YYYY').format('YYYY-MM-DD'));
                            await load();
                            return result.success;
                        } catch {
                            return false;
                        }
                    }}
                />
            </div>
        </div>}
        {reports === null
            ? <p>Loading&hellip;</p>
            : reports.length === 0
                ? <p>No reports have been run yet.</p>
                : <table>
                    <thead>
                        <tr><th>Date</th><th className='num'>Movements</th><th className='num'>Total Movement</th><th></th></tr>
                    </thead>
                    <tbody>
                        {reports.map((r, i) => <tr key={i}>
                            <td>{moment(r.date).format('DD-MMM-YYYY')}</td>
                            <td className='num'>
                                {r.movementCount.toLocaleString()}
                                {r.newCount > 0 && <span className='badge badge--new'>{r.newCount} New</span>}
                                {r.existingCount > 0 && <span className='badge badge--existing'>{r.existingCount} Existing</span>}
                                {r.returningCount > 0 && <span className='badge badge--returning'>{r.returningCount} Returning</span>}
                            </td>
                            <td className='num'>{r.totalAbsMovement.toLocaleString()}</td>
                            <td>
                                <a href={api().getHoldingsMovementReportUrl(props.ticker, props.exchange, moment(r.date).format('YYYY-MM-DD'))}
                                    target='_blank' rel='noopener noreferrer'>View Report</a>
                            </td>
                        </tr>)}
                    </tbody>
                </table>}
    </Wrapper>
}

export default HoldingsMovementReports;
